import {
  normaliseAuState,
  type ContactFilter,
  type ZohoContact,
  type AuStateCode,
} from '@deltasuite/zoho-client';
import { useMemo } from 'react';

interface ResolvedFilter {
  states: ReadonlySet<AuStateCode>;
  companyTypes: ReadonlySet<string>;
  hasMobile: boolean;
  lastActivityBefore: number | null;
  lastActivityAfter: number | null;
}

function resolveFilter(f: ContactFilter): ResolvedFilter {
  return {
    states: new Set(f.state ?? []),
    companyTypes: new Set(f.companyType ?? []),
    hasMobile: f.hasMobile === true,
    lastActivityBefore: f.lastActivityBefore ? Date.parse(f.lastActivityBefore) : null,
    lastActivityAfter: f.lastActivityAfter ? Date.parse(f.lastActivityAfter) : null,
  };
}

function matches(contact: ZohoContact, r: ResolvedFilter): boolean {
  if (r.states.size > 0) {
    const code = normaliseAuState(contact.Mailing_State);
    if (!code || !r.states.has(code)) return false;
  }
  if (r.companyTypes.size > 0) {
    if (!contact.Company_Type || !r.companyTypes.has(contact.Company_Type)) return false;
  }
  if (r.hasMobile && !contact.Mobile) return false;
  if (r.lastActivityBefore !== null) {
    if (!contact.Last_Activity_Time) return false;
    if (Date.parse(contact.Last_Activity_Time) >= r.lastActivityBefore) return false;
  }
  if (r.lastActivityAfter !== null) {
    if (!contact.Last_Activity_Time) return false;
    if (Date.parse(contact.Last_Activity_Time) <= r.lastActivityAfter) return false;
  }
  return true;
}

export function useFilterMatches(
  contacts: readonly ZohoContact[],
  filter: ContactFilter,
): ZohoContact[] {
  return useMemo(() => {
    const resolved = resolveFilter(filter);
    return contacts.filter((c) => matches(c, resolved));
  }, [contacts, filter]);
}
